import React, { useState, useEffect } from 'react';
import { Radio, Button, InputNumber, Typography, Card, Space, message, Select, Alert, Tag, Spin } from 'antd';
import { PlusOutlined, QuestionCircleOutlined, DeleteOutlined, LockOutlined } from '@ant-design/icons';
import { AddPlanToIBUserApi, GetActiveIbPlanList, GetPlanListApi, getUnassignAccountTypeApi, getUnassignApprovedAccountTypeApi, SaveTempPlanApi, UpdateIbRequestApi } from '../../../api/request/ib/ibapproved'
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import LoadableButton from '../../../components/buttons/LoadableButton';

const { Title, Text } = Typography;
const { Option } = Select;

const IbConfig = () => {
  const { id, type: approveType, planId } = useParams();
  const [commissionType, setCommissionType] = useState('multilevel');
  const [selectedAccountType, setSelectedAccountType] = useState(null);
  const [staticCommissionRate, setStaticCommissionRate] = useState(0.00);
  const [accountTypes, setAccountTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isGettingCommission, setIsGettingCommission] = useState(false);

  const navigate = useNavigate();
  const [commissionLevels, setCommissionLevels] = useState([
    { id: 1, level: 1, name: 'Direct Referrals', rate: 0, unit: 'per lot' },
    { id: 2, level: 2, name: 'Sub-IB Referrals', rate: 0, unit: 'per lot' },
    { id: 3, level: 3, name: 'Tier 3 Referrals', rate: 0, unit: 'per lot' }
  ]);

  const [validationErrors, setValidationErrors] = useState({});

  // Get selected account type object
  const getSelectedAccountType = () => {
    return accountTypes.find(type => type.id === selectedAccountType);
  };

  // Get max commission for selected account type
  const getMaxCommission = () => {
    const accountType = getSelectedAccountType();
    return accountType ? accountType.maxCommission : 0;
  };

  // Check if editing is allowed based on parent commission type
  const isEditingAllowed = () => {
    const accountType = getSelectedAccountType();
    return accountType ? accountType?.parentCommissionType === 'static' : false;
  };

  // Get parent commission levels for readonly display
  const getParentCommissionLevels = () => {
    const accountType = getSelectedAccountType();
    return accountType?.parentCommissionLevels || [];
  };

  // Calculate total commission for multilevel
  const getTotalCommission = () => {
    if (!isEditingAllowed()) {
      const parentLevels = getParentCommissionLevels();
      return parentLevels.reduce((total, level) => total + (level.rate || 0), 0);
    }
    return commissionLevels.reduce((total, level) => total + (level.rate || 0), 0);
  };

  // Reset commission type and data when account type changes
  useEffect(() => {
    if (selectedAccountType) {
      const accountType = getSelectedAccountType();
      if (accountType?.parentCommissionType === 'multilevel') {
        setCommissionType('multilevel');
        // Set readonly levels from parent configuration
        const parentLevels = accountType?.parentCommissionLevels.map((level, index) => ({
          id: index + 1,
          level: level.level,
          name: level.name,
          rate: level.rate,
          unit: level.unit
        }));
        setCommissionLevels(parentLevels);
      } else {
        // Reset to default for static parent type
        setCommissionType('multilevel');
        setCommissionLevels([
          { id: 1, level: 1, name: 'Direct Referrals', rate: 0, unit: 'per lot' },
          { id: 2, level: 2, name: 'Sub-IB Referrals', rate: 0, unit: 'per lot' },
          { id: 3, level: 3, name: 'Tier 3 Referrals', rate: 0, unit: 'per lot' }
        ]);
      }
    }
  }, [selectedAccountType]);

  // Validation logic
  useEffect(() => {
    if (!selectedAccountType || !isEditingAllowed()) {
      setValidationErrors({});
      return;
    }

    const maxCommission = getMaxCommission();
    const errors = {};

    if (commissionType === 'static') {
      if (staticCommissionRate > maxCommission) {
        errors.static = `Commission rate cannot exceed ${maxCommission} for selected account type`;
      }
    } else {
      const totalCommission = getTotalCommission();
      if (totalCommission > maxCommission) {
        errors.multilevel = `Total commission (${totalCommission.toFixed(2)}) cannot exceed ${maxCommission} for selected account type`;
      }
    }

    setValidationErrors(errors);
  }, [commissionType, staticCommissionRate, commissionLevels, selectedAccountType]);

  const addNewLevel = () => {
    if (!isEditingAllowed()) return;

    const newLevel = {
      id: Date.now(),
      level: commissionLevels.length + 1,
      name: `Level ${commissionLevels.length + 1} Referrals`,
      rate: 0,
      unit: 'per lot'
    };
    setCommissionLevels([...commissionLevels, newLevel]);
  };

  const deleteLevel = (id) => {
    if (!isEditingAllowed()) return;

    if (commissionLevels.length <= 1) {
      message.warning('At least one commission level is required');
      return;
    }

    const updatedLevels = commissionLevels
      .filter(level => level.id !== id)
      .map((level, index) => ({
        ...level,
        level: index + 1,
        name: `Level ${index + 1} Referrals`
      }));

    setCommissionLevels(updatedLevels);
  };

  const updateCommissionRate = (id, newRate) => {
    if (!isEditingAllowed()) return;

    setCommissionLevels(commissionLevels.map(level =>
      level.id === id ? { ...level, rate: newRate || 0 } : level
    ));
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);
      if (!selectedAccountType) {
        message.error('Please select an account type');
        return;
      }

      if (Object.keys(validationErrors).length > 0) {
        message.error('Please fix validation errors before saving');
        return;
      }

      const accountType = getSelectedAccountType();
      const configData = {
        planId: accountType?.id,
        userId: id,
        mode: commissionType === 'static' ? 1 : getSelectedAccountType()?.parentCommissionType == 'static' ? 2 : 3,
        commission: commissionType === 'static' ? staticCommissionRate : 0,
        accountTypes: [getSelectedAccountType()?.id],
        multiLevelCommissions: commissionType === 'multilevel' ? commissionLevels.map(level => ({
          level: level.level,
          name: level.name,
          rate: level.rate,
          unit: level.unit
        })) : null,
      };
      if (approveType === "pending") {
        const { data } = await SaveTempPlanApi(configData);
        if (data?.success) {
          toast.success(data?.message);
          navigate(`/ib/ib_details/${approveType}/${id}`)
        } else {
          toast.error(data?.message);
        }
      } else {
        if (planId != 0) {
          const { data } = await UpdateIbRequestApi(configData);
          if (data?.success) {
            toast.success(data?.message);
            navigate(`/ib/ib_details/${approveType}/${id}`)
          } else {
            toast.error(data?.message);
          }
        } else {
          const { data } = await AddPlanToIBUserApi([configData]);
          if (data?.success) {
            toast.success(data?.message);
            navigate(`/ib/ib_details/${approveType}/${id}`)
          } else {
            toast.error(data?.message);
          }
        }
      }
    } catch (err) {
      if (err?.response?.data?.message) {
        toast.error(err?.response?.data?.message)
      }
      navigate(`/ib/ib_details/${approveType}/${id}`)
    } finally {
      setIsLoading(false);
    }
  };

  const fillEditData = (data) => {
    data?.multiConfigs?.forEach(item => {
      if (item.planId == planId) {
        const arr = item?.levels?.map((item, index) => {
          return {
            ...item,
            id: index + 1
          }
        })
        setCommissionLevels(arr)
        setCommissionType('multilevel')
      }
    })
    data?.singleConfigs?.forEach(item => {
      if (item.planId == planId) {
        setStaticCommissionRate(item?.commission)
        setCommissionType('static')
      }
    })
  }

  const getUnassignAccountType = async () => {
    try {
      if (approveType === 'pending') {
        const { data } = await getUnassignAccountTypeApi(id, planId != 0 && planId);
        setAccountTypes(data?.data);

      } else {
        const { data } = await getUnassignApprovedAccountTypeApi(id, planId != 0 && planId);
        setAccountTypes(data?.data);
      }
    } catch (error) {
      console.error('Error fetching unassigned account types:', error);
    }
  }

  useEffect(() => {
    getUnassignAccountType();
  }, [id])

  const getCommissionData = async () => {
    try {
      setIsGettingCommission(true);
      if (approveType === "pending") {
        const { data } = await GetPlanListApi(id);
        fillEditData(data?.data)
      } else {
        const { data } = await GetActiveIbPlanList(id);
        fillEditData(data?.data)
      }
    } catch (err) { }
    finally {
      setIsGettingCommission(false);
    }
  }



  useEffect(() => {
    // Set selectedAccountType based on planId when accountTypes are loaded
    if (planId && planId != '0' && planId !== 'undefined' && accountTypes.length > 0) {
      // Find the account type that matches the planId
      const matchingAccountType = accountTypes.find(type => type.id === planId);
      if (matchingAccountType) {
        setIsGettingCommission(true);
        setSelectedAccountType(planId);
        getCommissionData()

      }
    }
  }, [planId, accountTypes]);


  return (
    <div className="p-6 min-h-screen">
      <div className="max-w-4xl mx-auto">
        {/* Account Type Selection */}
        <div className="mb-6">
          <Title level={4} className="mb-4">Account Type Selection</Title>
          <div className="max-w-md">
            <Select
              placeholder="Select Account Type"
              value={selectedAccountType}
              onChange={setSelectedAccountType}
              className="w-full"
              size="large"
              showSearch
              optionFilterProp="label"
              disabled={planId != 0}
            >
              {accountTypes.map(type => (
                <Option key={type.id} value={type.id} label={`${type.name} (Max: $${type.maxCommission})`}>
                  <div className="flex items-center justify-between">
                    <span>{type.name} (Max: ${type.maxCommission})</span>
                    <Tag color={type?.parentCommissionType === 'static' ? 'green' : 'blue'} className="ml-2">
                      {type?.parentCommissionType}
                    </Tag>
                  </div>
                </Option>
              ))}
            </Select>
          </div>
        </div>
        {(isGettingCommission && planId != 0) ? <div className="flex justify-center flex-col my-[20%]">
          <Spin size="large" />
          <p className="primary text-center mt-2">Loading...</p>
        </div> : <>
          {selectedAccountType && (
            <div className="mt-3 p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center justify-between">
                <Text className="text-blue-800">
                  <strong>Maximum Commission:</strong> ${getMaxCommission()} per lot for {getSelectedAccountType()?.name}
                </Text>
                <Tag color={getSelectedAccountType()?.parentCommissionType === 'static' ? 'green' : 'blue'}>
                  Parent: {getSelectedAccountType()?.parentCommissionType}
                </Tag>
              </div>
              {!isEditingAllowed() && (
                <div className="mt-2 flex items-center gap-2">
                  <LockOutlined className="text-orange-500" />
                  <Text className="text-orange-600">
                    <strong>Read-only mode:</strong> Parent account type uses multilevel commission structure
                  </Text>
                </div>
              )}
            </div>
          )}

          {/* Commission Type Section - Only show if editing is allowed */}
          {selectedAccountType && isEditingAllowed() && (
            <div className="mb-6">
              <Title level={4} className="mb-4">Commission Type</Title>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Static Commission */}
                <div className={`border rounded-lg p-4 hover:border-blue-300 transition-colors ${commissionType === 'static' ? 'border-blue-500 bg-blue-50' : ''
                  }`}>
                  <Radio
                    value="static"
                    checked={commissionType === 'static'}
                    onChange={(e) => setCommissionType(e.target.value)}
                    className="mb-2"
                    disabled={planId != 0}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-medium">💰</span>
                      <span className="font-medium">Static Commission</span>
                    </div>
                  </Radio>
                  <Text className="text-gray-600 block ml-6">
                    Fixed commission rate for all clients referred by this IB. Simple and straightforward.
                  </Text>
                </div>

                {/* Multilevel Commission */}
                <div className={`border rounded-lg p-4 hover:border-blue-300 transition-colors ${commissionType === 'multilevel' ? 'border-blue-500 bg-blue-50' : ''
                  }`}>
                  <Radio
                    value="multilevel"
                    checked={commissionType === 'multilevel'}
                    onChange={(e) => setCommissionType(e.target.value)}
                    className="mb-2"
                    disabled={planId != 0}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-lg">🏗️</span>
                      <span className="font-medium">Multilevel Commission</span>
                    </div>
                  </Radio>
                  <Text className="text-gray-600 block ml-6">
                    Hierarchical commission structure with different rates for different levels of referrals.
                  </Text>
                </div>
              </div>
            </div>
          )}

          {/* Static Commission Configuration */}
          {selectedAccountType && commissionType === 'static' && isEditingAllowed() && (
            <div>
              <Title level={4} className="mb-4">Static Commission Configuration</Title>

              {/* Validation Error */}
              {validationErrors.static && (
                <Alert
                  // message="Validation Error"
                  description={validationErrors.static}
                  type="error"
                  className="mb-4"
                />
              )}

              <div className="max-w-md">
                <div className="flex items-center gap-4 p-4 border rounded-lg">
                  <Text className="font-medium">Commission Rate:</Text>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500">$</span>
                    <InputNumber
                      value={staticCommissionRate}
                      onChange={setStaticCommissionRate}
                      min={0}
                      // max={selectedAccountType ? getMaxCommission() : undefined}
                      step={0.01}
                      precision={2}
                      className="w-24"
                      placeholder="0.00"
                      status={validationErrors.static ? 'error' : ''}
                    />
                    <Text className="text-gray-500">per lot</Text>
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <LoadableButton
                    className='bg-primary text-white px-4 py-2 rounded-md me-2'
                    onClick={handleSave}
                    isLoading={isLoading}
                    disabled={isLoading || !selectedAccountType || Object.keys(validationErrors).length > 0}
                    loadingLable="Saving..."
                    lable="Save Configuration"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Multilevel Commission Structure */}
          {selectedAccountType && commissionType === 'multilevel' && (
            <div>
              <div className="flex items-center gap-2 mb-3 mt-3">
                <Title level={4} className="mb-0">Multilevel Commission Structure</Title>
                <QuestionCircleOutlined className="text-gray-400" />
                {!isEditingAllowed() && (
                  <Tag color="orange" icon={<LockOutlined />}>
                    Read-only
                  </Tag>
                )}
              </div>

              {/* Validation Error */}
              {validationErrors.multilevel && (
                <Alert
                  // message="Validation Error"
                  description={validationErrors.multilevel}
                  type="error"
                  className="mb-4"
                />
              )}

              <div className="flex items-center gap-2 mb-6 p-3 bg-blue-50 rounded-lg">
                <span className="text-blue-600">ℹ️</span>
                <Text className="text-blue-800">
                  {isEditingAllowed()
                    ? 'Configure commission rates for different levels in the IB hierarchy. Each level represents the depth of referral chain.'
                    : 'Commission structure is inherited from parent account type and cannot be modified.'
                  }
                  {selectedAccountType && (
                    <span className="block mt-1">
                      <strong>Total: ${getTotalCommission().toFixed(2)} / ${getMaxCommission()}</strong>
                    </span>
                  )}
                </Text>
              </div>

              {/* Commission Levels Table */}
              <div className="space-y-3">
                {commissionLevels.map((level) => (
                  <div key={level.id} className={`flex items-center gap-4 p-4 border rounded-lg ${isEditingAllowed() ? 'bg-white' : 'bg-gray-50'
                    }`}>
                    {/* Level Badge */}
                    <div className={`px-3 py-1 rounded text-sm font-medium min-w-[80px] text-center ${isEditingAllowed() ? 'bg-gray-700 text-white' : 'bg-gray-400 text-white'
                      }`}>
                      Level {level.level}
                    </div>

                    {/* Level Name */}
                    <div className="flex-1">
                      <Text className="font-medium">Referral Level {level.level}</Text>
                      {!isEditingAllowed() && (
                        <Text className="text-gray-500 text-sm block">Inherited from parent</Text>
                      )}
                    </div>

                    {/* Rate Input */}
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">$</span>
                      <InputNumber
                        value={level.rate}
                        onChange={(value) => updateCommissionRate(level.id, value)}
                        min={0}
                        step={0.01}
                        precision={2}
                        className="w-24"
                        placeholder="0.00"
                        disabled={!isEditingAllowed()}
                        readOnly={!isEditingAllowed()}
                      />
                      <Text className="text-gray-500 min-w-[60px]">Per Lot</Text>
                    </div>

                    {/* Delete Button - Only show if editing is allowed */}
                    {isEditingAllowed() && (
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => deleteLevel(level.id)}
                        disabled={commissionLevels.length <= 1}
                        className="flex items-center justify-center"
                        title="Delete Level"
                      />
                    )}

                    {/* Lock icon for readonly */}
                    {!isEditingAllowed() && (
                      <LockOutlined className="text-gray-400" title="Read-only" />
                    )}
                  </div>
                ))}
              </div>

              {/* Add New Level Button - Only show if editing is allowed */}
              {isEditingAllowed() && (
                <div className="mt-4 text-center">
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={addNewLevel}
                    className="w-full md:w-auto"
                  >
                    Add New Level
                  </Button>
                </div>
              )}

              {/* Save Button */}
              <div className="mt-6 flex justify-end">
                <LoadableButton
                  className='bg-primary text-white px-4 py-2 rounded-md me-2'
                  onClick={handleSave}
                  isLoading={isLoading}
                  disabled={isLoading || !selectedAccountType || Object.keys(validationErrors).length > 0}
                  loadingLable={isEditingAllowed() ? "Saving..." : "Confirming..."}
                  lable={isEditingAllowed() ? 'Save Configuration' : 'Confirm Configuration'} />
              </div>
            </div>
          )}
        </>}
      </div>
    </div>
  );
};

export default IbConfig;